import React from 'react';
import styled from 'styled-components';
import glassTheme from '../../styles/glassTheme';
import type { Availability, Fraction, Measure } from '../../types/teacherAnalytics';

export interface FigureRow {
  id: string;
  label: string;
  value: number | null;
  availability: Availability;
  detail?: React.ReactNode;
  accent?: 'cyan' | 'purple';
}
type Scale = 'percent' | 'count';

const Figure = styled.figure`
  ${glassTheme.glass.light}
  margin: ${glassTheme.spacing.lg} 0; padding: ${glassTheme.spacing.md};
  min-width: 0; overflow-wrap: anywhere;
  figcaption { font-weight: 700; font-size: 1.1rem; }
  p { margin: ${glassTheme.spacing.sm} 0; }
  ul { list-style: none; margin: 0; padding: 0; display: grid; gap: ${glassTheme.spacing.lg}; }
  li { display: grid; grid-template-columns: minmax(120px, 1fr) minmax(0, 2fr); gap: ${glassTheme.spacing.sm} ${glassTheme.spacing.md}; }
  strong { display: block; }
  svg { display: block; width: 100%; height: 28px; margin-top: ${glassTheme.spacing.xs}; }
  small { display: block; color: ${glassTheme.colors.text.secondary}; }
  @media (max-width: 768px) { li { grid-template-columns: minmax(0, 1fr); } }
`;
const Axis = styled.div`
  display: grid; grid-template-columns: minmax(120px, 1fr) minmax(0, 2fr); gap: ${glassTheme.spacing.md};
  color: ${glassTheme.colors.text.secondary}; font-size: .8rem; margin-bottom: ${glassTheme.spacing.md};
  > div { grid-column: 2; display: flex; justify-content: space-between; gap: ${glassTheme.spacing.sm}; }
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); > div { grid-column: 1; } }
`;

export const rate = (v: number | null) => v === null || !Number.isFinite(v) ? '—' : `${Number((v * 100).toFixed(1))}%`;
const canPlot = (row: FigureRow, scale: Scale): boolean =>
  (row.availability === 'available' || row.availability === 'partial') &&
  row.value !== null && Number.isFinite(row.value) && row.value >= 0 &&
  (scale === 'percent' ? row.value <= 1 : Number.isSafeInteger(row.value));

export function fractionRow(id: string, label: string, value: Fraction): FigureRow {
  return {
    id, label, value: value.rate, availability: value.completed.availability,
    detail: `${value.completed.value ?? '—'}/${value.total} · 有完成状态记录 ${value.knownItems}/${value.total} · 状态未知 ${value.unknownItems}`
  };
}
export function attemptRow(id: string, label: string, value: Measure): FigureRow {
  return {id, label, value: value.value, availability: value.availability};
}

/** Visible labels carry every value; SVG is decorative and has no hover-only data. */
export function DataFigure({title, caption, rows, scale}: {
  title: string; caption: string; rows: FigureRow[]; scale: Scale;
}) {
  if (!rows.length) return null;
  const numeric = rows.filter(row => canPlot(row, scale));
  const maximum = scale === 'percent' ? 1 : Math.max(1, ...numeric.map(row => row.value!));
  return <Figure aria-label={title}>
    <figcaption>{title}</figcaption><p>{caption}</p>
    {numeric.length > 0 && <Axis aria-label="图表刻度"><div><span>0{scale === 'percent' ? '%' : ' 次'}</span>
      <span>{scale === 'percent' ? '100%' : `${maximum} 次`}</span></div></Axis>}
    <ul>{rows.map(row => {
      const plotted = canPlot(row, scale);
      const width = plotted ? row.value! / maximum * 200 : 0;
      const color = glassTheme.colors.accent[row.accent || (scale === 'count' ? 'purple' : 'cyan')];
      return <li key={row.id}>
        <div>{row.label}{row.detail && <small>{row.detail}</small>}</div>
        <div><strong>{row.availability === 'not_applicable' ? '不适用' :
          !plotted ? '—（数据不可用）' : scale === 'percent' ? rate(row.value) : `${row.value} 次`}
          {row.availability === 'partial' && ' · 部分记录'}</strong>
          {plotted && <svg viewBox="0 0 200 24" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <rect x="0" y="3" width="200" height="18" rx="3" fill={glassTheme.colors.border} />
            {[0, 100, 200].map(x => <line key={x} x1={x} x2={x} y1="0" y2="24" stroke={glassTheme.colors.text.muted} />)}
            <rect data-chart-bar="" x="0" y="3" width={width} height="18" rx="3" fill={color} />
          </svg>}
        </div>
      </li>;
    })}</ul>
  </Figure>;
}
